/**
 * File type constants for upload and download operations
 * Provides bidirectional mapping between MIME types and file extensions
 */

// Supported MIME types for file uploads
export const SUPPORTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'audio/mpeg',   // MP3
  'audio/wav',
  'audio/x-wav',
  'audio/ogg',
  'audio/flac',
  'application/pdf'
];

// MIME type to file extension mapping
export const MIME_TO_EXT = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/ogg': 'ogg',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/ogg': 'ogg',
  'audio/flac': 'flac',
  'application/pdf': 'pdf'
};

// File extension to MIME type mapping (bidirectional)
export const EXT_TO_MIME = {
  'png': 'image/png',
  'jpg': 'image/jpeg',
  'jpeg': 'image/jpeg',
  'gif': 'image/gif',
  'mp4': 'video/mp4',
  'webm': 'video/webm',
  // OGG can be audio or video; we treat it as audio for Music uploads.
  // Video uploads still work because we also allow 'video/ogg' in SUPPORTED_TYPES.
  'ogg': 'audio/ogg',
  'mp3': 'audio/mpeg',
  'wav': 'audio/wav',
  'flac': 'audio/flac',
  'pdf': 'application/pdf'
};

/**
 * Get file extension from MIME type
 * @param {string} mimeType - MIME type string
 * @returns {string} File extension without dot
 */
export const getExtensionFromMime = (mimeType) => {
  return MIME_TO_EXT[mimeType] || null;
};

/**
 * Get MIME type from file extension
 * @param {string} extension - File extension with or without dot
 * @returns {string} MIME type string
 */
export const getMimeFromExtension = (extension) => {
  // Remove leading dot if present
  const ext = extension.startsWith('.') ? extension.slice(1) : extension;
  return EXT_TO_MIME[ext.toLowerCase()] || null;
};

/**
 * Check if a MIME type is supported
 * @param {string} mimeType - MIME type to check
 * @returns {boolean} True if supported
 */
export const isSupportedMimeType = (mimeType) => {
  return SUPPORTED_TYPES.includes(mimeType);
};

/**
 * Check if a file extension is supported
 * @param {string} extension - File extension to check
 * @returns {boolean} True if supported
 */
export const isSupportedExtension = (extension) => {
  const ext = extension.startsWith('.') ? extension.slice(1) : extension;
  return EXT_TO_MIME[ext.toLowerCase()] !== undefined;
};

